import React from 'react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import { twJoin, twMerge } from 'tailwind-merge';
import { buildTrackingId } from '@/utils/tracking-helper';
import Button from '@/ui/components/shared/Button';

/** Which corner of the tile the control bar is pinned to. */
export type VideoControlsAnchor = 'bottom-left' | 'top-left';

export interface VideoControlsProps {
  playing: boolean;
  muted: boolean;
  showFullscreen: boolean;
  isFullscreen: boolean;
  /** Keeps the bar on screen while the video runs, for devices that cannot hover. */
  isAlwaysVisible?: boolean;
  /** The corner the bar sits in, for tiles whose overlay would otherwise cover the default one. */
  controlsAnchor?: VideoControlsAnchor;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onToggleFullscreen: () => void;
  trackingId?: string;
}

/** Every heroicon shares this signature, so one of them defines the accepted icon type. */
type VideoControlIcon = typeof PlayIcon;

export interface VideoControlButtonProps {
  label: string;
  onClick: () => void;
  Icon: VideoControlIcon;
  trackingId?: string;
  /** Merged over the round pill, for callers that place it or raise its contrast. */
  className?: string;
}

const CONTROL_CLASSES =
  'flex h-9 w-9 items-center justify-center rounded-full border-0 bg-neutral-100/60 p-0 text-white hover:bg-neutral-100/80 hover:text-white';

// `neutral-100` is pure black and the fullscreen wrapper paints the backdrop black, so the default
// pill would leave nothing but the glyphs. A lighter, more opaque fill plus a ring keeps the
// buttons readable over a dark backdrop as well as over a bright frame of the video itself.
const FULLSCREEN_CONTROL_CLASSES = 'bg-neutral-70/80 ring-1 ring-white/40 hover:bg-neutral-60/90';

const ICON_CLASSES = 'h-4 w-4';

const BAR_CLASSES = 'absolute z-30 flex items-center gap-2 transition-opacity duration-200';

/**
 * Bottom-left over the tile, where it sits below the editorial overlay; a tile whose overlay runs
 * along the bottom edge takes the top instead, which leaves the two blocks on opposite edges where
 * they can never overlap. A fullscreen element is sized to the screen, so the bar moves under the
 * letterboxed media and stays centred there whichever corner the caller picked. The anchor is
 * picked rather than appended, or both sets of offsets would end up on the element.
 */
const BAR_ANCHOR_CLASSES: Record<VideoControlsAnchor, string> = {
  'bottom-left': 'bottom-3 left-3',
  'top-left': 'top-3 left-3',
};
const BAR_FULLSCREEN_ANCHOR_CLASSES = 'bottom-6 left-1/2 -translate-x-1/2';

/**
 * The faded-out bar still sits above the full-surface tap target, so it has to stop taking
 * pointer events as well - `group-hover` never matches on a touch device.
 */
const BAR_HIDDEN_CLASSES = twJoin(
  'pointer-events-none opacity-0',
  'group-hover:pointer-events-auto group-hover:opacity-100',
  'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
);

export function VideoControlButton({
  label,
  onClick,
  Icon,
  trackingId,
  className,
}: Readonly<VideoControlButtonProps>) {
  return (
    <Button
      variant="secondaryBlack"
      className={twMerge(CONTROL_CLASSES, className)}
      onClick={onClick}
      aria-label={label}
      data-tile-element-id={trackingId}
    >
      <Icon className={ICON_CLASSES} aria-hidden="true" />
    </Button>
  );
}

export function VideoControls({
  playing,
  muted,
  showFullscreen,
  isFullscreen,
  isAlwaysVisible,
  controlsAnchor = 'bottom-left',
  onTogglePlay,
  onToggleMute,
  onToggleFullscreen,
  trackingId,
}: Readonly<VideoControlsProps>) {
  const t = useTranslations('Video');
  // Fading out is a hover affordance of the tile; in fullscreen the bar is the only way back out.
  const isHidden = playing && !isAlwaysVisible && !isFullscreen;
  const buttonClasses = isFullscreen ? FULLSCREEN_CONTROL_CLASSES : undefined;

  return (
    <div
      data-testid="video-player-controls"
      className={twJoin(
        BAR_CLASSES,
        isFullscreen ? BAR_FULLSCREEN_ANCHOR_CLASSES : BAR_ANCHOR_CLASSES[controlsAnchor],
        isHidden ? BAR_HIDDEN_CLASSES : 'opacity-100',
      )}
    >
      <VideoControlButton
        label={playing ? t('pause') : t('play')}
        onClick={onTogglePlay}
        Icon={playing ? PauseIcon : PlayIcon}
        trackingId={buildTrackingId(trackingId, 'play')}
        className={buttonClasses}
      />
      <VideoControlButton
        label={muted ? t('unmute') : t('mute')}
        onClick={onToggleMute}
        Icon={muted ? SpeakerXMarkIcon : SpeakerWaveIcon}
        trackingId={buildTrackingId(trackingId, 'mute')}
        className={buttonClasses}
      />
      {showFullscreen && (
        <VideoControlButton
          label={isFullscreen ? t('exitFullscreen') : t('enterFullscreen')}
          onClick={onToggleFullscreen}
          Icon={isFullscreen ? ArrowsPointingInIcon : ArrowsPointingOutIcon}
          trackingId={buildTrackingId(trackingId, 'fullscreen')}
          className={buttonClasses}
        />
      )}
    </div>
  );
}
